import { InjectionToken, Type } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap } from 'rxjs/operators';

/** Resolves an overlay page component, fetching its chunk on demand. */
export type OverlayComponentLoader = () => Promise<Type<unknown>>;

export interface LayoutOverlayLoaders {
  healthFacilities: OverlayComponentLoader;
  collectionQueues: OverlayComponentLoader;
  laboratorySectors: OverlayComponentLoader;
  clinics: OverlayComponentLoader;
  cashClosing: OverlayComponentLoader;
  vaccinesConfig: OverlayComponentLoader;
  patientRegistry: OverlayComponentLoader;
  solicitantes: OverlayComponentLoader;
  cashMovementReport: OverlayComponentLoader;
  creditDebitReport: OverlayComponentLoader;
  priceTables: OverlayComponentLoader;
}

/**
 * The overlay pages hosted by `LayoutComponent` are fetched the first time they
 * open instead of being imported by `LayoutModule`. Imported statically, every
 * overlay landed in the INITIAL bundle — paid on the login screen by every user,
 * whether or not they ever open it — and together they pushed the build past the
 * `initial` budget in `angular.json` (1.13 MB against 1.1 MB, pipeline
 * 20260923.2). A new overlay goes here, never into `LayoutModule.imports`.
 *
 * Injectable so the layout spec can mount lightweight stand-ins instead of the
 * real pages and their HTTP-backed services.
 */
export const LAYOUT_OVERLAY_LOADERS = new InjectionToken<LayoutOverlayLoaders>('LAYOUT_OVERLAY_LOADERS', {
  providedIn: 'root',
  factory: () => ({
    healthFacilities: () =>
      import('../features/health-facilities/pages/health-facilities-page/health-facilities-page.component').then(
        (m) => m.HealthFacilitiesPageComponent
      ),
    collectionQueues: () =>
      import('../features/collection-queues/pages/collection-queues-page/collection-queues-page.component').then(
        (m) => m.CollectionQueuesPageComponent
      ),
    laboratorySectors: () =>
      import('../features/laboratory-sectors/pages/laboratory-sectors-page/laboratory-sectors-page.component').then(
        (m) => m.LaboratorySectorsPageComponent
      ),
    clinics: () =>
      import('../features/clinics/pages/clinics-page/clinics-page.component').then((m) => m.ClinicsPageComponent),
    cashClosing: () =>
      import('../features/cash-control/components/cash-closing-modal/cash-closing-modal.component').then(
        (m) => m.CashClosingModalComponent
      ),
    vaccinesConfig: () =>
      import('../features/vaccines-config/pages/vaccines-config-page/vaccines-config-page.component').then(
        (m) => m.VaccinesConfigPageComponent
      ),
    patientRegistry: () =>
      import('../features/patient/components/patient-registry-window/patient-registry-window.component').then(
        (m) => m.PatientRegistryWindowComponent
      ),
    solicitantes: () =>
      import('../features/solicitantes/components/solicitantes-modal/solicitantes-modal.component').then(
        (m) => m.SolicitantesModalComponent
      ),
    cashMovementReport: () =>
      import('../features/cash-control/components/cash-movement-report-modal/cash-movement-report-modal.component').then(
        (m) => m.CashMovementReportModalComponent
      ),
    creditDebitReport: () =>
      import('../features/cash-control/components/credit-debit-report-modal/credit-debit-report-modal.component').then(
        (m) => m.CreditDebitReportModalComponent
      ),
    priceTables: () =>
      import('../features/price-tables/components/price-tables-modal/price-tables-modal.component').then(
        (m) => m.PriceTablesModalComponent
      ),
  }),
});

/**
 * Maps an overlay's open state to the component to mount: the loaded page while
 * open, `null` (nothing mounted) while closed. `switchMap` drops a load that is
 * still in flight when the overlay closes, so a fast open→close never mounts a
 * stale page. A failed chunk fetch leaves the overlay unmounted instead of
 * killing the stream, so the next open retries.
 */
export function componentWhileOpen(
  open$: Observable<boolean>,
  load: OverlayComponentLoader
): Observable<Type<unknown> | null> {
  return open$.pipe(
    distinctUntilChanged(),
    switchMap((open) => (open ? from(load()).pipe(catchError(() => of(null))) : of(null)))
  );
}
